import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useEventParticipationSubmission } from "../../context/EventParticipationSubmissionManager";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

function EventParticipationFieldsForm() {
    const { eventId } = useParams();
    const history = useHistory();
    const {
        event,
        formData,
        updateCustomField,
        submitForm,
        errors,
        customFields,
        loading,
        updateFormData,
        hasLegalUrls,
    } = useEventParticipationSubmission();
    const [submitting, setSubmitting] = useState(false);
    const [localErrors, setLocalErrors] = useState({});

    // After refresh, state is wiped – redirect to first step to avoid submit errors
    useEffect(() => {
        if (loading) {
            return;
        }
        const hasNawData = formData.address?.trim();
        if (!hasNawData) {
            history.replace(`/event/${eventId}/participation-submission/naw`);
        }
    }, [loading, formData.address, eventId, history]);

    const renderLinks = () => {
        const disclaimerUrl = event?.disclaimerUrl ?? event?.organisation?.disclaimerUrl;
        const privacyStatementUrl = event?.privacyStatementUrl ?? event?.organisation?.privacyStatementUrl;
        const termsAndConditionsUrl = event?.termsAndConditionsUrl ?? event?.organisation?.termsAndConditionsUrl;

        const links = [];
        if (disclaimerUrl) {
            links.push({
                key: "disclaimer",
                name: "Disclaimer",
                url: disclaimerUrl,
            });
        }
        if (privacyStatementUrl) {
            links.push({
                key: "privacy",
                name: "Privacyverklaring",
                url: privacyStatementUrl,
            });
        }
        if (termsAndConditionsUrl) {
            links.push({
                key: "terms",
                name: "Algemene Voorwaarden",
                url: termsAndConditionsUrl,
            });
        }

        const linkComponents = links.map((link, index) => (
            <React.Fragment key={link.key}>
                <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "black", textDecoration: "underline" }}
                >
                    {link.name}
                </a>
                {index < links.length - 2 && ", "}
                {index === links.length - 2 && " en "}
            </React.Fragment>
        ));

        return linkComponents;
    };

    const validateForm = useCallback(() => {
        const newErrors = {};

        customFields.forEach(field => {
            if (field.required && field.enabled) {
                const value = formData.customFields[field.id];

                if (field.fieldType === "checkbox" && !value) {
                    newErrors[field.id] = `${field.fieldName} is verplicht`;
                } else if (field.fieldType === "number" && (value === null || value === undefined || value === "")) {
                    newErrors[field.id] = `${field.fieldName} is verplicht`;
                } else if (["text", "textarea", "select"].includes(field.fieldType) && (!value || value.trim() === "")) {
                    newErrors[field.id] = `${field.fieldName} is verplicht`;
                }
            }
        });

        // Check if any URLs are available and if acceptConditions is checked
        if (hasLegalUrls && !formData.acceptConditions) {
            newErrors.acceptConditions = "Je moet akkoord gaan met de voorwaarden voordat je kunt doorgaan";
        }

        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [customFields, formData.customFields, formData.acceptConditions, event]);


    const handleSubmit = async (event) => {
        event.preventDefault();

        if (validateForm()) {
            setSubmitting(true);

            try {
                const success = await submitForm();

                if (success) {
                    history.push(`/event/${eventId}/participation-submission/success`);
                }
            } catch (error) {
                console.error("Error submitting form:", error);
                setLocalErrors({
                    submit: "Er is een fout opgetreden bij het versturen van de aanvullende informatie. Neem contact op met de organisatie."
                });
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleChange = (fieldId, value) => {
        updateCustomField(fieldId, value);

        // Clear error when user interacts with field
        if (localErrors[fieldId]) {
            setLocalErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[fieldId];
                return newErrors;
            });
        }
    };

    const renderField = (field) => {
        if (!field.enabled) return null;

        const fieldValue = formData.customFields[field.id] || "";
        const hasError = !!localErrors[field.id];

        switch (field.fieldType) {
        case "text":
            return (
                <div className="mb-3" key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="form-label">
                        {field.fieldName}{field.required ? " *" : ""}
                    </label>
                    {field.explanation && (
                        <OverlayTrigger overlay={(props) => (
                            <Tooltip {...props}>
                                {field.explanation}
                            </Tooltip>
                        )}>
                            <i className="fa-solid fa-circle-info ml-2" style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                    )}
                    <input
                        type="text"
                        className={`form-control ${hasError ? "is-invalid" : ""}`}
                        id={`field-${field.id}`}
                        value={fieldValue}
                        onChange={(event) => handleChange(field.id, event.target.value)}
                    />
                    {hasError && (
                        <div className="invalid-feedback">{localErrors[field.id]}</div>
                    )}
                </div>
            );

        case "textarea":
            return (
                <div className="mb-3" key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="form-label">
                        {field.fieldName}{field.required ? " *" : ""}
                    </label>
                    {field.explanation && (
                        <OverlayTrigger overlay={(props) => (
                            <Tooltip {...props}>
                                {field.explanation}
                            </Tooltip>
                        )}>
                            <i className="fa-solid fa-circle-info ml-2" style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                    )}
                    <textarea
                        className={`form-control ${hasError ? "is-invalid" : ""}`}
                        id={`field-${field.id}`}
                        value={fieldValue}
                        onChange={(event) => handleChange(field.id, event.target.value)}
                        rows={3}
                    />
                    {hasError && (
                        <div className="invalid-feedback">{localErrors[field.id]}</div>
                    )}
                </div>
            );

        case "number":
            return (
                <div className="mb-3" key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="form-label">
                        {field.fieldName}{field.required ? " *" : ""}
                    </label>
                    {field.explanation && (
                        <OverlayTrigger overlay={(props) => (
                            <Tooltip {...props}>
                                {field.explanation}
                            </Tooltip>
                        )}>
                            <i className="fa-solid fa-circle-info ml-2" style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                    )}
                    <input
                        type="number"
                        className={`form-control ${hasError ? "is-invalid" : ""}`}
                        id={`field-${field.id}`}
                        value={fieldValue}
                        min={0}
                        step={1}
                        onChange={(event) => handleChange(field.id, event.target.value)}
                    />
                    {hasError && (
                        <div className="invalid-feedback">{localErrors[field.id]}</div>
                    )}
                </div>
            );

        case "select":
            return (
                <div className="mb-3" key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="form-label">
                        {field.fieldName}{field.required ? " *" : ""}
                    </label>
                    {field.explanation && (
                        <OverlayTrigger overlay={(props) => (
                            <Tooltip {...props}>
                                {field.explanation}
                            </Tooltip>
                        )}>
                            <i className="fa-solid fa-circle-info ml-2" style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                    )}
                    <select
                        className={`form-control form-select ${hasError ? "is-invalid" : ""}`}
                        id={`field-${field.id}`}
                        value={fieldValue}
                        onChange={(event) => handleChange(field.id, event.target.value)}
                    >
                        <option value="">Selecteer...</option>
                        {field.attributes && field.attributes.options && field.attributes.options.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    {hasError && (
                        <div className="invalid-feedback">{localErrors[field.id]}</div>
                    )}
                </div>
            );

        default:
            return null;
        }
    };

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="mb-0">Aanvullende informatie</h3>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    {loading ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Laden...</span>
                            </div>
                            <p>Aanvullende vragen laden...</p>
                        </div>
                    ) : (
                        <>
                            {customFields && customFields.length === 0 ? (
                                <div className="alert alert-info">
                                        Er zijn geen aanvullende vragen voor dit evenement.
                                </div>
                            ) : (
                                <div>
                                    <p>Vul de onderstaande informatie in om je inschrijving af te ronden.</p>
                                    {customFields && customFields.map(renderField)}
                                </div>
                            )}
                            {hasLegalUrls && (
                                <div className="form-group">
                                    <div className="custom-control custom-checkbox mb-2">
                                        <input
                                            type="checkbox"
                                            className={`custom-control-input ${localErrors.acceptConditions ? "is-invalid" : ""}`}
                                            id="acceptConditions"
                                            disabled={loading}
                                            checked={formData.acceptConditions}
                                            onChange={() => updateFormData("acceptConditions", !formData.acceptConditions)}
                                        />
                                        <label className="custom-control-label" htmlFor="acceptConditions">
                                            Ik ga akkoord met de{" "}
                                            {renderLinks()}
                                        </label>
                                        {localErrors.acceptConditions && (
                                            <div className="invalid-feedback">{localErrors.acceptConditions}</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {errors.submit && (
                        <div className="alert alert-danger">{errors.submit}</div>
                    )}

                    <div className="d-flex justify-content-between mt-4">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => history.push(`/event/${eventId}/participation-submission/vehicle`)}
                            disabled={submitting}
                        >
                                Terug
                        </button>
                        <button
                            type="submit"
                            className="btn btn-success"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                    Versturen...
                                </>
                            ) : (
                                "Inschrijving afronden"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EventParticipationFieldsForm;
